// src/components/AgencySelector.js
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useModal } from '../context/ModalContext';
import '../styles/AgencySelector.css';
import CustomSelect from './CustomSelect';

const AgencySelector = ({ user, onAgencyChange }) => {
  const modal = useModal();
  const [agencies, setAgencies] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [loading, setLoading] = useState(false);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (user && user.is_super_admin && !hasInitialized.current) {
      hasInitialized.current = true;
      fetchAgencies();
    }
  }, [user]);

  const fetchAgencies = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/super-admin/agencies', {
        withCredentials: true
      });
      const fetchedAgencies = response.data.agencies || [];
      // Sort agencies alphabetically by name
      const sortedAgencies = [...fetchedAgencies].sort((a, b) => {
        const nameA = (a.agency_name || '').toLowerCase();
        const nameB = (b.agency_name || '').toLowerCase();
        return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
      });
      setAgencies(sortedAgencies);

      // Check if there's a saved agency in localStorage (persists across browser sessions)
      const savedAgencyId = localStorage.getItem('selected_agency_id');
      let agencyToSelect = null;

      if (savedAgencyId && fetchedAgencies.find(c => c._id === savedAgencyId)) {
        // Restore from localStorage - user's manual selection
        agencyToSelect = fetchedAgencies.find(c => c._id === savedAgencyId);
      } else {
        // Default to Black Raven AFC (with space) if available, otherwise select the first agency
        const blackRavenAFC = fetchedAgencies.find(
          agency => agency.agency_name === 'Black Raven AFC' || agency.slug === 'black-raven-afc'
        );
        agencyToSelect = blackRavenAFC || fetchedAgencies[0];
      }

      if (agencyToSelect) {
        // Set agency on backend and in state
        await setAgencyWithoutCallback(agencyToSelect._id, fetchedAgencies);

        // Also notify parent component so OrganizationSelector knows which agency is active
        if (onAgencyChange) {
          onAgencyChange(agencyToSelect);
        }
      }
    } catch (error) {
      console.error('Error fetching agencies:', error);
    } finally {
      setLoading(false);
    }
  };

  // Set agency on backend without triggering the onAgencyChange callback
  // Used for initial load/session restore to prevent unnecessary refreshes
  const setAgencyWithoutCallback = async (agencyId, agenciesList = null) => {
    try {
      // Switch to specific agency on backend
      await axios.post('/api/super-admin/switch-agency', {
        agency_id: agencyId
      }, {
        withCredentials: true
      });

      // Use provided list or fall back to state
      const agencyList = agenciesList || agencies;
      const agency = agencyList.find(c => c._id === agencyId);
      setSelectedAgency(agency);

      // Save to localStorage (persists across browser sessions)
      localStorage.setItem('selected_agency_id', agencyId);
    } catch (error) {
      console.error('Error setting agency:', error);
    }
  };

  const handleAgencySwitch = async (agencyId, agenciesList = null) => {
    try {
      // Switch to specific agency
      const response = await axios.post('/api/super-admin/switch-agency', {
        agency_id: agencyId
      }, {
        withCredentials: true
      });

      // Use provided list or fall back to state
      const agencyList = agenciesList || agencies;
      const agency = agencyList.find(c => c._id === agencyId);
      setSelectedAgency(agency);

      // Save to localStorage (persists across browser sessions)
      localStorage.setItem('selected_agency_id', agencyId);

      // Only trigger onAgencyChange when user explicitly changes the dropdown
      if (onAgencyChange) {
        onAgencyChange(agency);
      }
    } catch (error) {
      console.error('Error switching agency:', error);
      modal.error('Failed to switch agency.\nPlease try again.');
    }
  };

  // For non-super-admins, show read-only agency badge
  if (!user || !user.is_super_admin) {
    const agencyName = user?.agency_name || 'No Agency';
    return (
      <div className="agency-badge">
        <span className="agency-label">Agency:</span>
        <span className="agency-name">{agencyName}</span>
      </div>
    );
  }

  // For super admins, show dropdown selector
  return (
    <div className="agency-selector">
      <label htmlFor="agency-select" className="agency-label">
        Active Agency:
      </label>
        <CustomSelect
          id="agency-select"
          className="agency-select"
          value={selectedAgency?._id || ''}
          onChange={(e) => handleAgencySwitch(e.target.value)}
          disabled={agencies.length === 0}
        >
          {!selectedAgency && <option value="">Select an agency...</option>}
          {agencies.map((agency) => (
            <option key={agency._id} value={agency._id}>
              {agency.agency_name}
            </option>
          ))}
        </CustomSelect>
    </div>
  );
};

export default AgencySelector;
